import { useId } from 'react';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { reference, type DocsParam } from '../../generated/reference';
import type { RememberedValue } from '../../schemas/docs-storage-schema';
import { exampleText, type TryItValues } from '../../schemas/try-it-form-schema';

/**
 * One path, query or header input. Values remembered from earlier responses are offered as
 * suggestions, and the one it started with says which operation it came from.
 */
export function ParamField({
  control,
  param,
  source,
  suggestions = [],
}: {
  control: Control<TryItValues>;
  param: DocsParam;
  /** Operation id the starting value was remembered from. */
  source?: string | undefined;
  suggestions?: readonly RememberedValue[];
}) {
  const { t } = useTranslation('docs');
  const listId = useId();
  const required = param.required || param.in === 'path';
  const from = source ? (reference.operations[source]?.summary ?? source) : null;

  return (
    <FormField
      control={control}
      name={`${param.in}.${param.name}`}
      render={({ field }) => (
        <FormItem className="gap-1.5">
          <FormLabel className="flex items-baseline gap-1.5 font-mono text-xs">
            {param.name}
            {required ? (
              <span className="text-destructive" title={t('tryIt.required')}>
                *<span className="sr-only">{t('tryIt.required')}</span>
              </span>
            ) : null}
            <span className="text-muted-foreground font-sans font-normal">{param.schema.type}</span>
          </FormLabel>
          <FormControl>
            <Input
              autoComplete="off"
              spellCheck={false}
              className="h-8 font-mono text-[13px]"
              placeholder={exampleText(param.example)}
              list={suggestions.length > 0 ? listId : undefined}
              {...field}
            />
          </FormControl>
          {suggestions.length > 0 ? (
            <datalist id={listId}>
              {suggestions.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {reference.operations[entry.from]?.summary ?? entry.from}
                </option>
              ))}
            </datalist>
          ) : null}
          {from ? (
            <FormDescription>{t('tryIt.fromOperation', { operation: from })}</FormDescription>
          ) : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
